import { Button } from "../../../ui/Button";
import { LAYOUT, TEXT_STYLES } from "../../../constants";
import { RunState, newRun, getMaxFloor } from "../../../state/runState";
import { EncounterScene } from "../../EncounterScene";
import { BaseScreen } from "../../../screens/BaseScreen";
import { Panel } from "../../../ui/Panel";
import { tagTextForAutomation } from "../../../automation/automationTags";
import { OVERWORLD_SCENE_KEY } from "../../OverworldScene";
import { newOverworldState } from "../../../state/overworldState";
import { SKILLS } from "../../../data/skills";
import { getPerkName } from "../../../data/cards";
import { navLog } from "../../../debugNavigation";
import type { ScreenContext } from "../../../screens/types";

function formatSkillSummary(run: RunState): string {
  const entries = Object.entries(run.player.skills);
  if (entries.length === 0) return "None";
  return entries
    .map(([skillId, level]) => `${SKILLS[skillId as keyof typeof SKILLS].name} Lv${level}`)
    .join(", ");
}

function formatPerkSummary(run: RunState): string {
  if (run.player.perks.length === 0) return "None";
  return run.player.perks.map((perkId) => getPerkName(perkId)).join(", ");
}

export class RunVictoryScreen extends BaseScreen {
  private run!: RunState;

  constructor(scene: EncounterScene) {
    super(scene, "RUN_VICTORY");
  }

  enter(context?: ScreenContext) {
    this.run = context?.run ?? newRun();

    const { width, height } = this.scene.scale;
    const buttonWidth = width - LAYOUT.screenPadding * 2;

    const title = this.scene.add
      .text(width / 2, LAYOUT.screenPadding, "Run Complete!", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "run-victory-title");

    const subtitle = this.scene.add
      .text(
        width / 2,
        LAYOUT.screenPadding + 56,
        `Cleared ${this.run.floor}/${getMaxFloor()} floors`,
        TEXT_STYLES.subtitle
      )
      .setOrigin(0.5, 0);
    this.addToContainer(subtitle);
    tagTextForAutomation(subtitle, "run-victory-subtitle");

    const panel = new Panel(
      this.scene,
      LAYOUT.screenPadding,
      height * 0.22,
      buttonWidth,
      320,
      "Run Summary"
    );
    this.addToContainer(panel);

    const summaryText = [
      `Floors cleared: ${this.run.floor}/${getMaxFloor()}`,
      `Encounters cleared: ${this.run.totalEncountersCleared}`,
      `Enemies defeated: ${this.run.totalEnemiesDefeated}`,
      `HP: ${this.run.player.hp}/${this.run.player.maxHp}`,
      `ATK: ${this.run.player.atk}`,
      `Skills: ${formatSkillSummary(this.run)}`,
      `Perks: ${formatPerkSummary(this.run)}`,
    ].join("\n");

    const text = this.scene.add
      .text(LAYOUT.panelPadding, 40, summaryText, TEXT_STYLES.body)
      .setOrigin(0, 0);
    panel.add(text);
    tagTextForAutomation(text, "run-victory-summary");

    const newRunButton = new Button(this.scene, LAYOUT.screenPadding, height * 0.72, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight,
      label: "New Run",
      automationName: "run-victory-new-run",
      onClick: () => {
        this.scene.overlay.hideBanner();
        this.startNewRun();
      },
    });
    this.addToContainer(newRunButton);
  }

  private startNewRun() {
    const run = newRun();
    const overworldState = newOverworldState(run);

    this.scene.overlay.transition(() => {
      navLog("RunVictoryScreen", "Starting overworld for new run.", { floor: run.floor });
      this.scene.scene.start(OVERWORLD_SCENE_KEY, { overworldState });
    });
  }
}
